import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import TopbarService from '../../services/topbar';
import { getUserData } from '../../selectors/user';
import toaster from '../../utils/toaster';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import TopbarMenu from './TopbarMenu';
import {
    getPartnerBadgeImage,
    getProfileDisplay,
    getRoleBadge,
    shouldShow2FA,
    shouldShowAccountPlan,
    shouldShowTeamManagement,
} from '../utils';
import {
    DEFAULT_USER_IMAGE,
    HEADERS,
    SHOW_ACCOUNT_SELECT_MODAL_EVENT,
    SHOW_CHANGE_PASSWORD_MODAL_EVENT,
} from '../consts';
import css from '../style.css';

const topbarService = new TopbarService();

function AccountWidget({ translate }) {
    const userData = useSelector(getUserData) || {};
    const [menuOpen, setMenuOpen] = useState(false);

    if (!userData.email) {
        return null;
    }

    const impersonatedOrganization = window.impersonatedOrganization || '';
    const { name, image } = getProfileDisplay(userData, impersonatedOrganization);
    const roleBadge = getRoleBadge(userData);
    const showPartnerBadge =
        !!impersonatedOrganization && roleBadge && ['Partner', 'Agency'].includes(roleBadge.roleText);

    const onLogout = () => {
        topbarService
            .logout()
            .then(() => window.location.reload())
            .catch(() => toaster.error(translate(`${HEADERS}.LOGOUT_ERROR`)));
    };

    return (
        <TopbarMenu
            ariaLabel="account menu"
            onOpenChange={setMenuOpen}
            toggle={
                <>
                    {image && (
                        <span className={css.avatarWrapper}>
                            <img className={css.avatar} src={image} alt="" />
                            {showPartnerBadge && (
                                <img
                                    className={css.partnerBadgeOverlay}
                                    src={getPartnerBadgeImage(userData) || DEFAULT_USER_IMAGE}
                                    alt=""
                                />
                            )}
                        </span>
                    )}
                    <span className={css.hideBelowSm}>{name}</span>
                    {roleBadge && (
                        <span className={css.warningChip}>
                            <Translate id={roleBadge.translationKey} />
                        </span>
                    )}
                    <span className={classNames(css.menuChevron, { [css.menuChevronOpen]: menuOpen })}>
                        <Icon
                            name="chevron-down"
                            size={IconSizes.MEDIUM}
                            variant={IconVariants.WHITE}
                            interactive={false}
                        />
                    </span>
                </>
            }
        >
            {impersonatedOrganization && (
                <>
                    <div className={css.menuItem}>
                        <img className={css.avatar} src={getPartnerBadgeImage(userData) || DEFAULT_USER_IMAGE} alt="" />
                        <span>
                            {`${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email}
                        </span>
                    </div>
                    <button
                        type="button"
                        className={css.menuItem}
                        onClick={() => window.dispatchEvent(new Event(SHOW_ACCOUNT_SELECT_MODAL_EVENT))}
                    >
                        <Translate id={`${HEADERS}.CHANGE_USER`} />
                    </button>
                </>
            )}
            {shouldShowAccountPlan(userData) && (
                <a className={css.menuItem} href="#/react/account-plan">
                    <Translate id={`${HEADERS}.ACCOUNT_PLAN`} />
                </a>
            )}
            {shouldShowTeamManagement(userData) && (
                <a className={css.menuItem} href="#/react/team">
                    <Translate
                        id={
                            userData.is_agencies_allowed ? `${HEADERS}.MANAGE_USERS_AND_AGENCIES` : 'STATIC.MANAGE_TEAM'
                        }
                    />
                </a>
            )}
            <button
                type="button"
                className={css.menuItem}
                onClick={() => window.dispatchEvent(new Event(SHOW_CHANGE_PASSWORD_MODAL_EVENT))}
            >
                <Translate id={`${HEADERS}.CHANGE_PASSWORD`} />
            </button>
            {shouldShow2FA(userData) && (
                <a className={css.menuItem} href="#/settings/2fa">
                    <Translate id={`${HEADERS}.TWO_FACTOR_AUTH`} />
                </a>
            )}
            <button type="button" className={css.menuItem} onClick={onLogout}>
                <Translate id={`${HEADERS}.LOGOUT`} />
            </button>
        </TopbarMenu>
    );
}

AccountWidget.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AccountWidget);
